
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { BarChart3, Settings, Loader2 } from "lucide-react";

interface Symbol {
  value: string;
  label: string;
  category: string;
}

interface AnalysisConfigCardProps {
  selectedSymbol: string;
  setSelectedSymbol: (symbol: string) => void;
  selectedLanguage: string;
  setSelectedLanguage: (language: string) => void;
  isAnalyzing: boolean;
  onAnalysis: () => void;
  symbols: Symbol[];
  isLoadingSymbols: boolean;
  symbolsError?: string;
}

export const AnalysisConfigCard = ({
  selectedSymbol,
  setSelectedSymbol,
  selectedLanguage,
  setSelectedLanguage,
  symbols,
  isLoadingSymbols,
  symbolsError
}: AnalysisConfigCardProps) => {
  const languages = [
    { value: 'vi', label: 'Tiếng Việt', flag: '🇻🇳' },
    { value: 'en', label: 'English', flag: '🇺🇸' },
    { value: 'zh', label: '中文', flag: '🇨🇳' },
    { value: 'ja', label: '日本語', flag: '🇯🇵' },
    { value: 'ko', label: '한국어', flag: '🇰🇷' }
  ];
  return (
    <Card className="relative overflow-hidden bg-gradient-to-br from-background via-background to-muted/20 border border-border/50 backdrop-blur-sm hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:-translate-y-1 group card-shine soft-glow">
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(34,197,94,0.05),transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="absolute inset-0 shimmer-slide opacity-0 group-hover:opacity-30 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      
      <CardHeader className="relative z-10 pb-4">
        <CardTitle className="text-foreground flex items-center gap-2 group-hover:text-emerald-600 transition-colors duration-300">
          <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-500 group-hover:bg-emerald-500/20 transition-all duration-300 floating-action">
            <BarChart3 className="h-5 w-5 icon-spin-hover" />
          </div>
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4 floating-action" />
            Analysis Configuration
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="relative z-10 space-y-6">
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-emerald-500 floating-action" />
            Select Symbol
          </label>
          <Select value={selectedSymbol} onValueChange={setSelectedSymbol} disabled={isLoadingSymbols}>
            <SelectTrigger className="bg-background/80 border-border/50 text-foreground hover:border-emerald-500/30 transition-all duration-200 backdrop-blur-sm card-hover-effect">
              <SelectValue placeholder={
                isLoadingSymbols ? "Loading symbols..." : 
                symbolsError ? "Error loading symbols" :
                "Choose a trading symbol"
              } />
              {isLoadingSymbols && <Loader2 className="h-4 w-4 animate-spin ml-2" />}
            </SelectTrigger>
            <SelectContent className="bg-background/95 border-border/50 backdrop-blur-md">
              {symbolsError ? (
                <SelectItem value="error" disabled className="text-red-500">
                  {symbolsError}
                </SelectItem>
              ) : symbols.length === 0 && !isLoadingSymbols ? (
                <SelectItem value="empty" disabled className="text-muted-foreground">
                  No symbols available
                </SelectItem>
              ) : (
                symbols.map((symbol) => (
                  <SelectItem 
                    key={symbol.value} 
                    value={symbol.value} 
                    className="text-foreground hover:bg-accent/80 transition-colors duration-200 card-hover-effect"
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="hover-scale">{symbol.label}</span>
                      <Badge variant="outline" className="ml-2 text-xs bg-muted/50 pulse-soft">
                        {symbol.category}
                      </Badge>
                    </div>
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground flex items-center gap-2">
            <Settings className="h-4 w-4 text-blue-500 floating-action" />
            Analysis Language
          </label>
          <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
            <SelectTrigger className="bg-background/80 border-border/50 text-foreground hover:border-blue-500/30 transition-all duration-200 backdrop-blur-sm card-hover-effect">
              <SelectValue placeholder="Choose analysis language" />
            </SelectTrigger>
            <SelectContent className="bg-background/95 border-border/50 backdrop-blur-md">
              {languages.map((language) => (
                <SelectItem 
                  key={language.value} 
                  value={language.value}
                  className="text-foreground hover:bg-accent/80 transition-colors duration-200 card-hover-effect"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base">{language.flag}</span>
                    <span className="hover-scale">{language.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
};
